import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  updateProfileSchema,
  type UpdateProfileForm,
} from '@/lib/schemas/profile';
import { useUpdateProfile, type ProfileMe } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

/**
 * Раздел ФИО + телефон + язык + таймзона.
 *
 * Backend: `PATCH /profile/me` — обязательные first_name + last_name,
 * остальное опционально. После save invalidate profile.me.
 */
export function ProfileSectionFio({ me }: { me: ProfileMe }) {
  const updateMut = useUpdateProfile();
  const [serverError, setServerError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<UpdateProfileForm>({
    resolver: zodResolver(updateProfileSchema),
    mode: 'onBlur',
    defaultValues: {
      first_name: me.first_name ?? '',
      last_name: me.last_name ?? '',
      middle_name: me.middle_name ?? '',
      phone: me.phone ?? '',
      preferred_language: me.preferred_language || 'ru',
      timezone: me.timezone || 'Europe/Moscow',
    },
  });

  const onSubmit = async (data: UpdateProfileForm) => {
    setServerError(null);
    try {
      await updateMut.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        middle_name: data.middle_name || null,
        phone: data.phone || null,
        preferred_language: data.preferred_language,
        timezone: data.timezone,
      });
      reset(data);
      setSavedAt(Date.now());
      window.setTimeout(() => setSavedAt(null), 3000);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.detail);
      else setServerError('Сетевая ошибка');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {serverError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}
      {savedAt && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>Сохранено</AlertDescription>
        </Alert>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="profile-last-name">Фамилия</Label>
        <Input
          id="profile-last-name"
          autoComplete="family-name"
          aria-invalid={!!errors.last_name}
          {...register('last_name')}
        />
        {errors.last_name && (
          <p className="text-destructive text-xs">{errors.last_name.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="profile-first-name">Имя</Label>
        <Input
          id="profile-first-name"
          autoComplete="given-name"
          aria-invalid={!!errors.first_name}
          {...register('first_name')}
        />
        {errors.first_name && (
          <p className="text-destructive text-xs">{errors.first_name.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="profile-middle-name">Отчество</Label>
        <Input
          id="profile-middle-name"
          autoComplete="additional-name"
          aria-invalid={!!errors.middle_name}
          {...register('middle_name')}
        />
        {errors.middle_name && (
          <p className="text-destructive text-xs">{errors.middle_name.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="profile-phone">Телефон</Label>
        <Input
          id="profile-phone"
          autoComplete="tel"
          placeholder="+7 (___) ___-__-__"
          aria-invalid={!!errors.phone}
          {...register('phone')}
        />
        {errors.phone && (
          <p className="text-destructive text-xs">{errors.phone.message}</p>
        )}
      </div>

      <Button
        type="submit"
        size="sm"
        disabled={isSubmitting || !isDirty}
        className="w-full"
      >
        {isSubmitting ? 'Сохраняем…' : 'Сохранить'}
      </Button>
    </form>
  );
}
